import { makeStyles } from '@material-ui/styles'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import type Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import ApiClient from 'api-client/ApiClient'
import { waitSeconds } from 'app/plugins/openstack/components/infrastructure/blueprint/helpers'
import type { RootState } from 'app/store'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import type { ModalStep } from 'core/elements/modal/StepperModalForm'
import StepperModalForm from 'core/elements/modal/StepperModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import {
  editMasterNodeLengthValidator,
  editMasterNodesLengthErrorMsg,
} from 'core/utils/fieldValidators'
import { listKaapiMachineDeployments } from 'k8s/components/kaapi/machine-deployment/actions'
import DataKeys from 'k8s/DataKeys'
import { ProgressStep, useClusterProgress } from '../access-control/hooks/useClusterProgress'
import PhysicalNodesTable from '../cluster/deployment/form-components/PhysicalNodesTable'
import { updateNodePools } from '../kaapi/clusters/actions'
import { kaapiMachineDeploymentsForClusterSelector } from '../kaapi/machine-deployment/selectors'
import {
  addClusterLabelMasterNodeToByoHost,
  addClusterLabelToByoHost,
  listByoHosts,
  removeClusterLabelToByoHost,
} from '../nodes/byoh/actions'
import ClusterMasterPhysicalNode from './ClusterMasterPhysicalNode'
import ClusterWorkerPhysicalNodes from './ClusterWorkerPhysicalNodes'

const { kubernetes } = ApiClient.getInstance()

const initialValues = {
  physicalNodes: [],
  masterPhysicalNodes: [],
}

interface AddNodeGroupsModalProps {
  onClose: () => void
  clusterName: string
  isClusterCustomControlPlane: boolean
  masterReplicas?: number
}

const AddNodeGroupsModal = ({
  onClose,
  isClusterCustomControlPlane,
  clusterName,
  masterReplicas,
}: AddNodeGroupsModalProps) => {
  const classes = useStyles()
  const initialParamsValues = {
    ...initialValues,
    controlPlaneType: isClusterCustomControlPlane ? 'custom' : 'managed',
  }
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialParamsValues)
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant

  const [error, setError] = useState(null)
  const [masterNodesError, setMasterNodesError] = useState(null)
  const [workerNodesError, setWorkerNodesError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const {
    progressDetails,
    updateProgress,
    resetProgress: resetClusterProgress,
  } = useClusterProgress({
    initLabel: 'Updating Cluster',
  })

  // Fetch existing Machine Deployments
  const { reload: reloadMachineDeployments } = useListAction(listKaapiMachineDeployments)
  const machineDeploymentsSelector = useMemo(
    () =>
      kaapiMachineDeploymentsForClusterSelector({
        clusterName,
      }),
    [clusterName],
  )
  const machineDeployments = useSelector(machineDeploymentsSelector) || []
  const { reload: reloadByoHosts } = useListAction(listByoHosts)

  // Update Actions
  const {
    update: updateNodePool,
    updating: updatingNodePool,
    error: updateNodePoolError,
  } = useUpdateAction(updateNodePools)
  const {
    update: addClusterLabelToByoHostFn,
    updating: addClusterLabelToByoHostUpdating,
    error: addClusterLabelToByoHostError,
  } = useUpdateAction(addClusterLabelToByoHost)
  const {
    update: removeClusterLabelToByoHostFn,
    updating: removeClusterLabelToByoHostUpdating,
    error: removeClusterLabelToByoHostError,
  } = useUpdateAction(removeClusterLabelToByoHost)
  const {
    update: addClusterLabelMasterNodeToByoHostFn,
    updating: addingClusterLabelMasterNodeToByoHost,
    error: addClusterLabelMasterNodeToByoHostError,
  } = useUpdateAction(addClusterLabelMasterNodeToByoHost)

  const hasError =
    !!addClusterLabelToByoHostError ||
    !!removeClusterLabelToByoHostError ||
    !!updateNodePoolError ||
    !!addClusterLabelMasterNodeToByoHostError

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const errorObj =
      addClusterLabelToByoHostError ||
      removeClusterLabelToByoHostError ||
      updateNodePoolError ||
      addClusterLabelMasterNodeToByoHostError

    setError({
      title: errorObj?.title || 'Error adding new nodes',
      message: errorObj?.message || 'An error occurred while adding new nodes. Please try again.',
    })
  }, [
    hasError,
    addClusterLabelToByoHostError,
    removeClusterLabelToByoHostError,
    addClusterLabelMasterNodeToByoHostError,
    updateNodePoolError,
  ])

  const handleError = useCallback(
    (error: any, message: string, step?: ProgressStep) => {
      setLoading(false)
      setError(error || { title: 'Error', message })
      if (step) {
        updateProgress(step, ProgressStatus.Failed)
      }
    },
    [updateProgress],
  )
  const updatedReplicas = useMemo(
    () => machineDeployments[0]?.spec?.replicas + params?.physicalNodes?.length,
    [machineDeployments, params.physicalNodes],
  )
  const updatedMasterReplicas = useMemo(
    () => masterReplicas + params?.masterPhysicalNodes?.length,
    [masterReplicas, params?.masterPhysicalNodes],
  )

  const steps: ModalStep[] = useMemo(
    () => [
      {
        stepId: 1,
        headerTitle: 'Add Control Plane Nodes',
        description:
          'To ensure high availability, your cluster must have an odd number of total control plane nodes.',
        component: (
          <div className={classes.formSection}>
            <div className={classes.statCardRow}>
              <div className={classes.statCard}>
                <Text variant="caption1" className={classes.statCardLabel}>
                  Current Control Plane Nodes
                </Text>
                <Text variant="body2" className={classes.statCardValue}>
                  {masterReplicas || 0}
                </Text>
              </div>
              <div className={classes.statCard}>
                <Text variant="caption1" className={classes.statCardLabel}>
                  Nodes Added
                </Text>
                <Text variant="body2" className={classes.statCardValue}>
                  {params?.masterPhysicalNodes?.length || 0}
                </Text>
              </div>
              <div className={classes.statCard}>
                <Text variant="caption1" className={classes.statCardLabel}>
                  Total Control Plane Nodes
                </Text>
                <Text variant="body2" className={classes.statCardValue}>
                  {updatedMasterReplicas || 0}
                </Text>
              </div>
            </div>
            <ClusterMasterPhysicalNode
              params={params}
              getParamsUpdater={getParamsUpdater}
              setError={setMasterNodesError}
              masterPhysicalNodeErrors={masterNodesError}
              setMasterPhysicalNodeErrors={setMasterNodesError}
            />
          </div>
        ),
        stepValidator: () => {
          if (
            !editMasterNodeLengthValidator.validate(
              params?.masterPhysicalNodes,
              updatedMasterReplicas,
            )
          ) {
            return {
              success: false,
              error: {
                title: 'Error',
                message: editMasterNodesLengthErrorMsg,
              },
            }
          }
          return { success: true }
        },
      },
      {
        stepId: 2,
        headerTitle: 'Add Worker Nodes',
        description: 'Select worker nodes for your cluster.',
        component: (
          <div className={classes.formSection}>
            <ClusterWorkerPhysicalNodes
              params={params}
              getParamsUpdater={getParamsUpdater}
              setError={setWorkerNodesError}
              physicalNodeErrors={workerNodesError}
              setPhysicalNodeErrors={setWorkerNodesError}
            />
          </div>
        ),
        stepValidator: () => {
          if (!params?.physicalNodes?.length && !params?.masterPhysicalNodes?.length) {
            return {
              success: false,
              error: {
                title: 'Error',
                message: 'Please select atleast one worker node or control plane node',
              },
            }
          }
          return { success: true }
        },
      },
    ],
    [params, getParamsUpdater, masterNodesError, workerNodesError, classes.formSection],
  )

  const handleSubmit = async () => {
    setError(null)
    setLoading(true)
    setShowProgress(true)

    // Update the selected nodes
    const res = await Promise.all(
      params.physicalNodes.map((node) =>
        addClusterLabelToByoHostFn({ name: node?.name, clusterName }),
      ),
    )

    let masterResponse
    if (isClusterCustomControlPlane) {
      masterResponse = await Promise.all(
        params.masterPhysicalNodes.map((node) =>
          addClusterLabelMasterNodeToByoHostFn({
            name: node?.name,
            clusterName,
          }),
        ),
      )
    }
    // If any of the promises failed, return
    if (res?.some((item) => !item?.success)) {
      handleError(null, 'Failed to update labels on worker nodes', ProgressStep.Init)
      return
    }
    if (masterResponse?.some((item) => !item?.success)) {
      handleError(null, 'Failed to update labels on control plane nodes', ProgressStep.Init)
      return
    }

    updateProgress(ProgressStep.Init, ProgressStatus.Success)

    // Update the machine deployment
    const { success } = await updateNodePool({
      namespace,
      clusterName,
      body: [
        {
          op: 'replace',
          path: `/spec/topology/workers/machineDeployments/0/replicas`,
          value: updatedReplicas,
        },
        ...(isClusterCustomControlPlane
          ? [
              {
                op: 'replace',
                path: `/spec/topology/controlPlane/replicas`,
                value: updatedMasterReplicas,
              },
            ]
          : []),
      ],
    })

    if (success) {
      updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)
      let isNodeGroupUpdated = false
      const maxAttempts = 60 // 5 minutes timeout (60 * 5 seconds)
      let attempts = 0

      while (!isNodeGroupUpdated && attempts < maxAttempts) {
        attempts++
        const updatedMachineDeployments = await kubernetes.getAllCustomResources(
          DataKeys.KaapiMachineDeployments,
        )

        const nodeGroupUpdated = updatedMachineDeployments?.find(
          (md) => md?.metadata?.name === machineDeployments?.[0]?.metadata?.name,
        )

        if (updatedReplicas === nodeGroupUpdated?.spec?.replicas) {
          isNodeGroupUpdated = true
          updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
          setIsProgressComplete(true)
          setLoading(false)
        } else {
          await waitSeconds(5)
        }
      }
      if (!isNodeGroupUpdated) {
        setLoading(false)
        throw new Error('Node groups are taking too long to get deployed')
      }
    } else {
      await Promise.all(
        params.physicalNodes.map((node) => removeClusterLabelToByoHostFn({ name: node?.name })),
      )
      handleError(null, 'Failed to update labels on worker nodes', ProgressStep.ControlPlane)
    }

    if (!hasError) {
      // Reload the machine deployments and machines
      reloadByoHosts(true, false)
      reloadMachineDeployments(true, false)
    }
  }

  const submitting =
    addClusterLabelToByoHostUpdating ||
    removeClusterLabelToByoHostUpdating ||
    updatingNodePool ||
    loading ||
    addingClusterLabelMasterNodeToByoHost

  const handleClose = useCallback(() => {
    setIsProgressComplete(false)
    setShowProgress(false)
    onClose()
  }, [onClose])

  const resetProgress = useCallback(() => {
    setIsProgressComplete(false)
    setShowProgress(false)
    resetClusterProgress()
  }, [resetClusterProgress])

  if (!isClusterCustomControlPlane) {
    return (
      <ModalForm
        open
        title="Add Worker Nodes"
        maxWidth={820}
        className={classes.modal}
        onSubmit={showProgress || isProgressComplete ? handleClose : handleSubmit}
        submitTitle={showProgress || isProgressComplete ? 'Finish' : 'Add Worker Nodes'}
        error={error}
        submitting={submitting}
        disableSubmit={params.physicalNodes.length === 0}
        onClose={onClose}
        onBackButtonClick={resetProgress}
        showBackButton={error && showProgress}
        data-testid="add-physical-nodes-modal"
      >
        {!showProgress && !isProgressComplete ? (
          <>
            <Text variant="body2">Select the worker nodes to add to the cluster.</Text>
            <br />
            <ClusterWorkerPhysicalNodes
              params={params}
              getParamsUpdater={getParamsUpdater}
              setError={setWorkerNodesError}
              physicalNodeErrors={workerNodesError}
              setPhysicalNodeErrors={setWorkerNodesError}
            />
          </>
        ) : (
          <StepProgress
            progressDetails={progressDetails}
            title={`Node group update is in progress.
                    Please wait and do not close this dialog, or the update will be cancelled.`}
          />
        )}
      </ModalForm>
    )
  }

  return (
    <StepperModalForm
      steps={steps}
      title="Add Physical Nodes"
      maxWidth={820}
      open
      onClose={onClose}
      onSubmit={showProgress || isProgressComplete ? handleClose : handleSubmit}
      error={error}
      resetError={() => setError(null)}
      submitting={submitting}
      params={params}
      isProgressComplete={isProgressComplete}
      closeProgressScreen={handleClose}
      showProgress={showProgress}
      resetProgress={resetProgress}
      data-testid="add-physical-nodes-stepper-modal"
      customStepProgressComponent={
        <StepProgress
          progressDetails={progressDetails}
          title={`Node group update is in progress.
                  Please wait and do not close this dialog, or the update will be cancelled.`}
        />
      }
    />
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
  formSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(2),
  },
  statCardRow: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  statCard: {
    flex: 1,
    display: 'grid',
    gridGap: theme.spacing(0.5),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.5),
  },
  statCardLabel: {
    color: theme.palette.grey[500],
    justifySelf: 'center',
  },
  statCardValue: {
    fontWeight: 600,
    justifySelf: 'center',
  },
}))

export default AddNodeGroupsModal
